// ============================================================================
// A zip's entries without downloading it (docs/areas/files.md): read the end
// of the file for the end-of-central-directory record (and its zip64
// locator), then the central directory itself, with two or three Range
// requests. Nothing is decompressed or extracted. Pure parsing over byte
// ranges; tested in zip.test.ts with zips built in the test.
// ============================================================================

export interface ZipEntry {
  name: string
  size: number
  compressed: number
  dir: boolean
  modified?: string
}

export interface ZipListing {
  entries: ZipEntry[]
  /** entries the directory names in all (the list may be capped) */
  total: number
  comment?: string
}

/** Reads bytes [start, end) of the file. */
export type RangeReader = (start: number, end: number) => Promise<Uint8Array>

const EOCD = 0x06054b50
const EOCD64 = 0x06064b50
const LOC64 = 0x07064b50
const CEN = 0x02014b50
/** the EOCD record is 22 bytes plus a comment of at most 65,535 */
const TAIL = 22 + 0xffff
/** a central directory past this is not read (a listing, not an archive tool) */
const MAX_DIR = 32 * 1024 * 1024

const dosDate = (date: number, time: number): string | undefined => {
  const y = 1980 + (date >> 9)
  const mo = (date >> 5) & 15
  const d = date & 31
  if (!mo || !d) return undefined
  const p = (n: number) => String(n).padStart(2, '0')
  return `${y}-${p(mo)}-${p(d)}T${p(time >> 11)}:${p((time >> 5) & 63)}:${p((time & 31) * 2)}`
}

const utf8 = new TextDecoder('utf-8')
const latin = new TextDecoder('latin1')

/** List a zip of `size` bytes; at most `cap` entries are returned. Throws on what is not a zip. */
export const listZip = async (read: RangeReader, size: number, cap = 2000): Promise<ZipListing> => {
  const tailStart = Math.max(0, size - TAIL)
  const tail = await read(tailStart, size)
  const dv = new DataView(tail.buffer, tail.byteOffset, tail.byteLength)
  let at = -1
  for (let i = tail.length - 22; i >= 0; i--) {
    if (dv.getUint32(i, true) === EOCD) {
      at = i
      break
    }
  }
  if (at < 0) throw new Error('not a zip (no end of central directory)')
  let total = dv.getUint16(at + 10, true)
  let dirSize = dv.getUint32(at + 12, true)
  let dirOffset = dv.getUint32(at + 16, true)
  const commentLen = dv.getUint16(at + 20, true)
  const comment = commentLen ? utf8.decode(tail.subarray(at + 22, at + 22 + commentLen)) : undefined
  // zip64: the 32-bit fields are saturated and a locator sits just before the record
  if ((total === 0xffff || dirSize === 0xffffffff || dirOffset === 0xffffffff) && at >= 20) {
    if (dv.getUint32(at - 20, true) === LOC64) {
      const recAt = Number(dv.getBigUint64(at - 20 + 8, true))
      const rec = await read(recAt, recAt + 56)
      const rv = new DataView(rec.buffer, rec.byteOffset, rec.byteLength)
      if (rv.getUint32(0, true) !== EOCD64) throw new Error('a broken zip64 directory')
      total = Number(rv.getBigUint64(32, true))
      dirSize = Number(rv.getBigUint64(40, true))
      dirOffset = Number(rv.getBigUint64(48, true))
    }
  }
  if (dirSize > MAX_DIR) throw new Error('the zip lists too much to show')
  if (dirOffset + dirSize > size) throw new Error('a broken zip (its directory runs past the end)')
  const dir =
    dirOffset >= tailStart
      ? tail.subarray(dirOffset - tailStart, dirOffset - tailStart + dirSize)
      : await read(dirOffset, dirOffset + dirSize)
  const v = new DataView(dir.buffer, dir.byteOffset, dir.byteLength)
  const entries: ZipEntry[] = []
  let p = 0
  for (let n = 0; n < total && p + 46 <= dir.length; n++) {
    if (v.getUint32(p, true) !== CEN) throw new Error('a broken zip (bad directory entry)')
    const flags = v.getUint16(p + 8, true)
    const time = v.getUint16(p + 12, true)
    const date = v.getUint16(p + 14, true)
    let compressed = v.getUint32(p + 20, true)
    let usize = v.getUint32(p + 24, true)
    const nameLen = v.getUint16(p + 28, true)
    const extraLen = v.getUint16(p + 30, true)
    const commLen = v.getUint16(p + 32, true)
    const nameBytes = dir.subarray(p + 46, p + 46 + nameLen)
    const name = (flags & 0x800 ? utf8 : latin).decode(nameBytes)
    // zip64 sizes ride in the extra field (id 0x0001), uncompressed first
    if (usize === 0xffffffff || compressed === 0xffffffff) {
      let e = p + 46 + nameLen
      const end = e + extraLen
      while (e + 4 <= end) {
        const id = v.getUint16(e, true)
        const len = v.getUint16(e + 2, true)
        if (id === 1) {
          let q = e + 4
          if (usize === 0xffffffff) {
            usize = Number(v.getBigUint64(q, true))
            q += 8
          }
          if (compressed === 0xffffffff) compressed = Number(v.getBigUint64(q, true))
          break
        }
        e += 4 + len
      }
    }
    if (entries.length < cap)
      entries.push({ name, size: usize, compressed, dir: name.endsWith('/'), modified: dosDate(date, time) })
    p += 46 + nameLen + extraLen + commLen
  }
  return { entries, total, ...(comment ? { comment } : {}) }
}

/** A reader over a URL with Range requests (the bucket answers 206). */
export const rangeReader =
  (url: string): RangeReader =>
  async (start, end) => {
    const res = await fetch(url, { headers: { Range: `bytes=${start}-${end - 1}` } })
    if (!res.ok) throw new Error(`could not read the zip (${res.status})`)
    const buf = new Uint8Array(await res.arrayBuffer())
    // a server that ignored Range sent it all: take the slice
    return res.status === 200 ? buf.subarray(start, end) : buf
  }
